import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Layers, Rows3, Table2 } from 'lucide-react';
import { useLots } from '../hooks/useLots.js';
import { formatCurrency, formatDate } from '../lib/format.js';
import {
  STATUS_DIMENSIONS,
  CONDITION_DIMENSIONS,
  SCAN_DIMENSIONS,
  buildSegments,
  dimensionTotal,
  lotAgeMonths,
  ecoulementPct,
} from '../lib/lotsHelpers.js';
import EcoulementBadge from '../components/EcoulementBadge.jsx';
import LotStackedBar from '../components/LotStackedBar.jsx';

// ── Tri ───────────────────────────────────────────────────────────────────────

const SORT_DEFAULT = { col: 'lot_number', dir: 'desc' };

function sortRows(rows, col, dir) {
  return [...rows].sort((a, b) => {
    let va, vb;
    if (col === 'ecoulement') {
      va = ecoulementPct(a.status_sold, a.product_count);
      vb = ecoulementPct(b.status_sold, b.product_count);
    } else if (col === 'cost_ht') {
      // null acquisition tombe en dernier en desc, en premier en asc — on neutralise.
      va = a.acquisition?.cost_ht_cents ?? -1;
      vb = b.acquisition?.cost_ht_cents ?? -1;
    } else if (col === 'cost_recovery') {
      va = a.acquisition?.cost_recovery_pct ?? -1;
      vb = b.acquisition?.cost_recovery_pct ?? -1;
    } else {
      va = a[col] ?? 0;
      vb = b[col] ?? 0;
    }
    if (va < vb) return dir === 'asc' ? -1 : 1;
    if (va > vb) return dir === 'asc' ? 1 : -1;
    return 0;
  });
}

// ── En-tête triable ───────────────────────────────────────────────────────────

function Th({
  col,
  label,
  title,
  sortState,
  onSort,
  className = '',
  rowSpan,
  colSpan,
  align = 'right',
}) {
  const sortable = Boolean(col && sortState && onSort);
  const active = sortable && sortState.col === col;
  return (
    <th
      rowSpan={rowSpan}
      colSpan={colSpan}
      className={`px-2 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap select-none ${
        align === 'left' ? 'text-left' : align === 'center' ? 'text-center' : 'text-right'
      } ${sortable ? 'cursor-pointer hover:text-slate-900 transition-colors' : 'cursor-default'} ${className}`}
      title={title}
      onClick={sortable ? () => onSort(col) : undefined}
      aria-sort={sortable && active ? (sortState.dir === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      {label}
      {active && <span className="ml-0.5 text-slate-400">{sortState.dir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );
}

// Seuils pour mettre en évidence en rouge les valeurs critiques d'un lot
function criticalCellClass(value, total, threshold) {
  if (!total || value == null) return 'text-slate-500';
  return value / total >= threshold ? 'text-rose-600 font-semibold' : 'text-slate-700';
}

// Labels courts pour le thead détaillé
const SHORT = {
  status_live:       { label: 'Live',  title: 'Live' },
  status_sold:       { label: 'Sold',  title: 'Sold' },
  status_okref:      { label: 'OKR',   title: 'OKREF' },
  status_pending:    { label: 'Pen',   title: 'Pending' },
  status_waiting:    { label: 'Wait',  title: 'Waiting_for_Price' },
  status_stale:      { label: 'Stale', title: 'Stale' },
  cond_new:          { label: 'Neuf',  title: 'Neuf' },
  cond_good:         { label: 'Good',  title: 'Good' },
  cond_normal:       { label: 'Norm',  title: 'Normal' },
  cond_bad:          { label: 'Bad',   title: 'Bad' },
  scan_in_stock:     { label: 'IN',    title: 'IN_STOCK' },
  scan_out_of_stock: { label: 'Out',   title: 'Out of stock' },
  scan_not_found:    { label: 'NF',    title: 'Not Found' },
};

const VIEW_STORAGE_KEY = 'lots-view-mode';

// ── Page ──────────────────────────────────────────────────────────────────────

export default function LotsAnalysisPage() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useLots();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(SORT_DEFAULT);
  const [viewMode, setViewMode] = useState('compact'); // 'compact' | 'detailed'

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(VIEW_STORAGE_KEY);
      if (saved === 'compact' || saved === 'detailed') setViewMode(saved);
    } catch {
      /* localStorage indispo (SSR / mode privé) → on garde le défaut */
    }
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_STORAGE_KEY, viewMode);
    } catch {
      /* idem */
    }
  }, [viewMode]);

  const rows = data?.rows ?? data ?? [];

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().replace(/^LT/i, '');
    return rows.filter((r) => String(r.lot_number).startsWith(q));
  }, [rows, search]);

  const sorted = useMemo(
    () => sortRows(filtered, sort.col, sort.dir),
    [filtered, sort]
  );

  function handleSort(col) {
    setSort((s) =>
      s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' }
    );
  }

  const totalLots = filtered.length;
  const totalCatalog = filtered.reduce((acc, r) => acc + (r.catalog_value_cents ?? 0), 0);
  const totalCa = filtered.reduce((acc, r) => acc + (r.ca_real_cents ?? 0), 0);
  const totalSold = filtered.reduce((acc, r) => acc + (r.status_sold ?? 0), 0);
  const totalPieces = filtered.reduce((acc, r) => acc + (r.product_count ?? 0), 0);
  const globalEcoulement = totalPieces > 0 ? Math.round((totalSold / totalPieces) * 100) : 0;

  const detailed = viewMode === 'detailed';

  return (
    <div className="space-y-5">

      {/* Header inline — densité plutôt que cartes décoratives */}
      <div>
        <h2 className="text-base font-semibold text-slate-900">Vision par lot</h2>
        <p className="mt-1 text-sm text-slate-600 tabular-nums">
          <span className="font-semibold text-slate-900">{totalLots.toLocaleString('fr-FR')}</span> lot{totalLots > 1 ? 's' : ''}
          <span className="mx-2 text-slate-300">·</span>
          catalogue <span className="font-semibold text-slate-900">{formatCurrency(totalCatalog)}</span>
          <span className="mx-2 text-slate-300">·</span>
          CA réel <span className="font-semibold text-slate-900">{formatCurrency(totalCa)}</span>
          <span className="mx-2 text-slate-300">·</span>
          écoulement <span className="font-semibold text-emerald-700">{globalEcoulement} %</span>
        </p>
      </div>

      {/* Toolbar : search + view toggle */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Numéro de lot (ex : 1155 ou LT1155)"
          className="flex-1 min-w-[180px] max-w-xs px-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
          aria-label="Filtrer par numéro de lot"
        />
        <div className="hidden md:inline-flex items-center rounded-md border border-slate-300 bg-white p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setViewMode('compact')}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
              !detailed ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-900'
            }`}
            aria-pressed={!detailed}
          >
            <Rows3 size={13} aria-hidden /> Compacte
          </button>
          <button
            type="button"
            onClick={() => setViewMode('detailed')}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded transition-colors ${
              detailed ? 'bg-slate-900 text-white' : 'text-slate-500 hover:text-slate-900'
            }`}
            aria-pressed={detailed}
          >
            <Table2 size={13} aria-hidden /> Détaillée
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-sm text-rose-700">
          Impossible de charger les lots : {error.message}
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-12 bg-slate-100 animate-pulse rounded" />
          ))}
        </div>
      )}

      {!isLoading && !error && sorted.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <Layers size={32} className="text-slate-300" />
          <p className="text-sm text-slate-500">Aucun lot trouvé.</p>
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="text-xs text-slate-600 hover:text-slate-900 underline underline-offset-2"
            >
              Effacer le filtre
            </button>
          )}
        </div>
      )}

      {/* Mobile cards — vue compacte uniquement (pas de mode détaillé sur mobile) */}
      {!isLoading && sorted.length > 0 && (
        <div className="md:hidden space-y-2">
          {sorted.map((lot) => {
            const productCount = lot.product_count || 0;
            const age = lotAgeMonths(lot.lot_date_min);
            const statusSegments = buildSegments(lot, STATUS_DIMENSIONS);
            const condSegments = buildSegments(lot, CONDITION_DIMENSIONS);
            const scanSegments = buildSegments(lot, SCAN_DIMENSIONS);
            return (
              <button
                key={lot.lot_number}
                type="button"
                onClick={() => navigate(`/ventes/lots/${lot.lot_number}`)}
                className="w-full text-left bg-white rounded-lg border border-slate-200 px-4 py-3 active:bg-slate-50 transition-colors"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="flex items-baseline gap-2">
                    <span className="font-semibold text-slate-900 text-sm">LT{lot.lot_number}</span>
                    <span className="text-xs text-slate-400 tabular-nums">
                      {productCount} pièce{productCount > 1 ? 's' : ''}
                      {age !== null ? ` · ${age} m` : ''}
                    </span>
                  </div>
                  <ChevronRight size={16} className="text-slate-300 shrink-0" aria-hidden />
                </div>

                <div className="mt-3 space-y-1.5">
                  <DimRow label="Statut"    segments={statusSegments} total={dimensionTotal(lot, STATUS_DIMENSIONS)} />
                  <DimRow label="Condition" segments={condSegments}   total={dimensionTotal(lot, CONDITION_DIMENSIONS)} />
                  <DimRow label="Scan"      segments={scanSegments}   total={dimensionTotal(lot, SCAN_DIMENSIONS)} />
                </div>

                <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between text-xs tabular-nums">
                  <div className="flex gap-3 text-slate-500">
                    <span>Cat. <span className="text-slate-800 font-medium">{formatCurrency(lot.catalog_value_cents)}</span></span>
                    <span>CA <span className="text-slate-800 font-medium">{formatCurrency(lot.ca_real_cents)}</span></span>
                  </div>
                  <EcoulementBadge sold={lot.status_sold} total={productCount} />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Desktop table */}
      {!isLoading && sorted.length > 0 && (
        <div className="hidden md:block bg-white rounded-md border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            {detailed ? (
              <DetailedThead sort={sort} onSort={handleSort} />
            ) : (
              <CompactThead sort={sort} onSort={handleSort} />
            )}
            <tbody className="divide-y divide-slate-100">
              {sorted.map((lot) =>
                detailed ? (
                  <DetailedRow key={lot.lot_number} lot={lot} navigate={navigate} />
                ) : (
                  <CompactRow key={lot.lot_number} lot={lot} navigate={navigate} />
                )
              )}
              {/* Ligne de totaux */}
              <tr className="bg-slate-50/70 text-slate-700 text-xs tabular-nums">
                <td className="px-4 py-2 font-semibold sticky left-0 bg-slate-50/70 uppercase tracking-wider text-[11px]">
                  Total
                </td>
                {detailed ? (
                  <DetailedTotalsCells filtered={filtered} />
                ) : (
                  <CompactTotalsCells filtered={filtered} totalLots={totalLots} totalCatalog={totalCatalog} totalCa={totalCa} totalSold={totalSold} totalPieces={totalPieces} globalEcoulement={globalEcoulement} />
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Mobile dim row (label + bar + count) ──────────────────────────────────────

function DimRow({ label, segments, total }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-wider text-slate-400 w-14 shrink-0">{label}</span>
      <div className="flex-1 min-w-0">
        <LotStackedBar segments={segments} total={total} height={6} />
      </div>
      <span className="text-[10px] tabular-nums text-slate-400 w-8 text-right shrink-0">{total}</span>
    </div>
  );
}

// ── Vue compacte (8 colonnes) ─────────────────────────────────────────────────

function CompactThead({ sort, onSort }) {
  return (
    <thead>
      <tr className="border-b border-slate-200 bg-slate-50/60">
        <Th col="lot_number"          align="left"  label="Lot"       sortState={sort} onSort={onSort} className="sticky left-0 bg-slate-50/60 pl-4 z-10" />
        <Th col="product_count"       align="right" label="Pièces"    sortState={sort} onSort={onSort} />
        <Th                            align="left"  label="Statut"     sortState={sort} onSort={onSort} className="pl-3" />
        <Th                            align="left"  label="Condition"  sortState={sort} onSort={onSort} className="pl-3" />
        <Th                            align="left"  label="Scan"       sortState={sort} onSort={onSort} className="pl-3" />
        <Th col="catalog_value_cents" align="right" label="Catalogue" sortState={sort} onSort={onSort} />
        <Th col="ca_real_cents"       align="right" label="CA réel"   sortState={sort} onSort={onSort} />
        <Th col="ecoulement"          align="right" label="Écoul."    sortState={sort} onSort={onSort} className="pr-4" />
      </tr>
    </thead>
  );
}

function CompactRow({ lot, navigate }) {
  const productCount = lot.product_count || 0;
  const age = lotAgeMonths(lot.lot_date_min);
  const statusSegments = buildSegments(lot, STATUS_DIMENSIONS);
  const condSegments = buildSegments(lot, CONDITION_DIMENSIONS);
  const scanSegments = buildSegments(lot, SCAN_DIMENSIONS);
  const statusTotal = dimensionTotal(lot, STATUS_DIMENSIONS);
  const condTotal = dimensionTotal(lot, CONDITION_DIMENSIONS);
  const scanTotal = dimensionTotal(lot, SCAN_DIMENSIONS);
  const stale = lot.status_stale ?? 0;
  const bad = lot.cond_bad ?? 0;
  const nf = lot.scan_not_found ?? 0;

  return (
    <tr
      onClick={() => navigate(`/ventes/lots/${lot.lot_number}`)}
      className="hover:bg-slate-50 cursor-pointer transition-colors group"
      title={lot.lot_date_min ? `Acquisition : ${formatDate(lot.lot_date_min)}` : undefined}
    >
      <td className="px-4 py-2 font-semibold text-slate-900 whitespace-nowrap sticky left-0 bg-white group-hover:bg-slate-50">
        LT{lot.lot_number}
      </td>
      <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">
        <span className="font-medium text-slate-800">{lot.product_count ?? '—'}</span>
        {age !== null && (
          <span className="block text-[10px] text-slate-400">{age} m</span>
        )}
      </td>
      <CompactDimCell segments={statusSegments} total={statusTotal} critical={stale} criticalLabel="Stale" criticalThreshold={0.30} />
      <CompactDimCell segments={condSegments}   total={condTotal}   critical={bad}   criticalLabel="Bad"   criticalThreshold={0.10} />
      <CompactDimCell segments={scanSegments}   total={scanTotal}   critical={nf}    criticalLabel="NF"    criticalThreshold={0.20} />
      <td className="px-2 py-2 text-right tabular-nums text-slate-700 whitespace-nowrap">
        {formatCurrency(lot.catalog_value_cents)}
      </td>
      <td className="px-2 py-2 text-right tabular-nums font-semibold text-slate-900 whitespace-nowrap">
        {formatCurrency(lot.ca_real_cents)}
      </td>
      <td className="px-3 py-2 text-right pr-4">
        <div className="inline-flex items-center gap-1">
          <EcoulementBadge sold={lot.status_sold} total={lot.product_count} />
          {lot.acquisition?.cost_recovery_pct != null && lot.acquisition.cost_recovery_pct >= 100 && (
            <span
              className="text-emerald-600 text-xs"
              title={`Lot rentabilisé (cost recovery : ${lot.acquisition.cost_recovery_pct} %)`}
              aria-label="Lot rentabilisé"
            >▲</span>
          )}
        </div>
      </td>
    </tr>
  );
}

function CompactDimCell({ segments, total, critical, criticalLabel, criticalThreshold }) {
  const isCritical = total > 0 && critical / total >= criticalThreshold;
  return (
    <td className="px-3 py-2 min-w-[120px]">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <LotStackedBar segments={segments} total={total} height={6} />
        </div>
        {isCritical && (
          <span className="text-[10px] tabular-nums font-semibold text-rose-600 whitespace-nowrap">
            {critical} {criticalLabel}
          </span>
        )}
      </div>
    </td>
  );
}

function CompactTotalsCells({ totalLots, totalCatalog, totalCa, totalSold, totalPieces, globalEcoulement }) {
  return (
    <>
      <td className="px-2 py-2 text-right font-semibold text-slate-700">{totalPieces.toLocaleString('fr-FR')}</td>
      <td className="px-3 py-2" />
      <td className="px-3 py-2" />
      <td className="px-3 py-2" />
      <td className="px-2 py-2 text-right font-semibold text-slate-700">{formatCurrency(totalCatalog)}</td>
      <td className="px-2 py-2 text-right font-semibold text-slate-900">{formatCurrency(totalCa)}</td>
      <td className="px-3 py-2 text-right pr-4 font-semibold text-emerald-700">{globalEcoulement} %</td>
    </>
  );
}

// ── Vue détaillée (19 colonnes, thead groupé sur 2 lignes) ────────────────────

function DetailedThead({ sort, onSort }) {
  return (
    <thead>
      {/* Ligne 1 : groupes */}
      <tr className="border-b border-slate-100 bg-slate-50/60 text-slate-400">
        <Th rowSpan={2} col="lot_number"    align="left"  label="Lot"    sortState={sort} onSort={onSort} className="sticky left-0 bg-slate-50/60 pl-4 z-10" />
        <Th rowSpan={2} col="product_count" align="right" label="Pièces" sortState={sort} onSort={onSort} />
        <Th colSpan={STATUS_DIMENSIONS.length}    align="center" label="Statut"    className="bg-slate-50 text-slate-400 normal-case tracking-wider border-l border-slate-200" />
        <Th colSpan={CONDITION_DIMENSIONS.length} align="center" label="Condition" className="bg-slate-50 text-slate-400 normal-case tracking-wider border-l border-slate-200" />
        <Th colSpan={SCAN_DIMENSIONS.length}      align="center" label="Scan"      className="bg-slate-50 text-slate-400 normal-case tracking-wider border-l border-slate-200" />
        <Th colSpan={5}                            align="center" label="Financier"  className="bg-slate-50 text-slate-400 normal-case tracking-wider border-l border-slate-200" />
      </tr>
      {/* Ligne 2 : colonnes individuelles */}
      <tr className="border-b border-slate-200 bg-slate-50/30">
        {STATUS_DIMENSIONS.map((d, i) => (
          <Th key={d.key} col={d.key} label={SHORT[d.key].label} title={SHORT[d.key].title}
              sortState={sort} onSort={onSort}
              className={i === 0 ? 'border-l border-slate-200' : ''} />
        ))}
        {CONDITION_DIMENSIONS.map((d, i) => (
          <Th key={d.key} col={d.key} label={SHORT[d.key].label} title={SHORT[d.key].title}
              sortState={sort} onSort={onSort}
              className={i === 0 ? 'border-l border-slate-200' : ''} />
        ))}
        {SCAN_DIMENSIONS.map((d, i) => (
          <Th key={d.key} col={d.key} label={SHORT[d.key].label} title={SHORT[d.key].title}
              sortState={sort} onSort={onSort}
              className={i === 0 ? 'border-l border-slate-200' : ''} />
        ))}
        <Th col="catalog_value_cents" label="Catalogue" sortState={sort} onSort={onSort} className="border-l border-slate-200" />
        <Th col="cost_ht"             label="Coût HT"   sortState={sort} onSort={onSort} title="Coût d'acquisition HT" />
        <Th col="ca_real_cents"       label="CA réel"   sortState={sort} onSort={onSort} />
        <Th col="cost_recovery"       label="Récup."    sortState={sort} onSort={onSort} title="Cost recovery — CA réel / coût HT" />
        <Th col="ecoulement"          label="Écoul."    sortState={sort} onSort={onSort} className="pr-4" />
      </tr>
    </thead>
  );
}

function DetailedRow({ lot, navigate }) {
  const productCount = lot.product_count || 0;
  const age = lotAgeMonths(lot.lot_date_min);
  return (
    <tr
      onClick={() => navigate(`/ventes/lots/${lot.lot_number}`)}
      className="hover:bg-slate-50 cursor-pointer transition-colors group"
    >
      <td className="px-4 py-2 font-semibold text-slate-900 whitespace-nowrap sticky left-0 bg-white group-hover:bg-slate-50">
        LT{lot.lot_number}
      </td>
      <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">
        <span className="font-medium text-slate-800">{lot.product_count ?? '—'}</span>
        {age !== null && (
          <span className="block text-[10px] text-slate-400">{age} m</span>
        )}
      </td>
      {STATUS_DIMENSIONS.map((d, i) => {
        const cls = d.key === 'status_stale'
          ? criticalCellClass(lot[d.key] ?? 0, productCount, 0.30)
          : 'text-slate-700';
        return (
          <td key={d.key} className={`px-2 py-2 text-right tabular-nums ${cls} ${i === 0 ? 'border-l border-slate-100' : ''}`}>
            {lot[d.key] ?? 0}
          </td>
        );
      })}
      {CONDITION_DIMENSIONS.map((d, i) => {
        const cls = d.key === 'cond_bad'
          ? criticalCellClass(lot[d.key] ?? 0, productCount, 0.10)
          : 'text-slate-700';
        return (
          <td key={d.key} className={`px-2 py-2 text-right tabular-nums ${cls} ${i === 0 ? 'border-l border-slate-100' : ''}`}>
            {lot[d.key] ?? 0}
          </td>
        );
      })}
      {SCAN_DIMENSIONS.map((d, i) => {
        const cls = d.key === 'scan_not_found'
          ? criticalCellClass(lot[d.key] ?? 0, productCount, 0.20)
          : 'text-slate-700';
        return (
          <td key={d.key} className={`px-2 py-2 text-right tabular-nums ${cls} ${i === 0 ? 'border-l border-slate-100' : ''}`}>
            {lot[d.key] ?? 0}
          </td>
        );
      })}
      <td className="px-2 py-2 text-right tabular-nums text-slate-700 whitespace-nowrap border-l border-slate-100">
        {formatCurrency(lot.catalog_value_cents)}
      </td>
      <td className="px-2 py-2 text-right tabular-nums text-slate-700 whitespace-nowrap">
        {lot.acquisition?.cost_ht_cents != null ? formatCurrency(lot.acquisition.cost_ht_cents) : '—'}
      </td>
      <td className="px-2 py-2 text-right tabular-nums font-semibold text-slate-900 whitespace-nowrap">
        {formatCurrency(lot.ca_real_cents)}
      </td>
      <td className="px-2 py-2 text-right tabular-nums whitespace-nowrap">
        {lot.acquisition?.cost_recovery_pct != null ? (
          <span className={lot.acquisition.cost_recovery_pct >= 100 ? 'text-emerald-700 font-semibold' : 'text-slate-700'}>
            {lot.acquisition.cost_recovery_pct} %
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right pr-4">
        <EcoulementBadge sold={lot.status_sold} total={lot.product_count} />
      </td>
    </tr>
  );
}

function DetailedTotalsCells({ filtered }) {
  const sumCol = (k) => filtered.reduce((acc, r) => acc + (r[k] ?? 0), 0);
  const totalPieces = sumCol('product_count');
  const totalCatalog = sumCol('catalog_value_cents');
  const totalCa = sumCol('ca_real_cents');
  const totalSold = sumCol('status_sold');
  const totalCostHt = filtered.reduce((acc, r) => acc + (r.acquisition?.cost_ht_cents ?? 0), 0);
  const globalEcoulement = totalPieces > 0 ? Math.round((totalSold / totalPieces) * 100) : 0;
  const globalCostRecovery = totalCostHt > 0 ? Math.round((totalCa / totalCostHt) * 100) : null;
  const cells = [
    ...STATUS_DIMENSIONS.map((d, i) => ({ k: d.key, first: i === 0 })),
    ...CONDITION_DIMENSIONS.map((d, i) => ({ k: d.key, first: i === 0 })),
    ...SCAN_DIMENSIONS.map((d, i) => ({ k: d.key, first: i === 0 })),
  ];
  return (
    <>
      <td className="px-2 py-2 text-right font-semibold text-slate-700">{totalPieces.toLocaleString('fr-FR')}</td>
      {cells.map(({ k, first }) => (
        <td key={k} className={`px-2 py-2 text-right font-semibold text-slate-700 ${first ? 'border-l border-slate-100' : ''}`}>
          {sumCol(k)}
        </td>
      ))}
      <td className="px-2 py-2 text-right font-semibold text-slate-700 whitespace-nowrap border-l border-slate-100">{formatCurrency(totalCatalog)}</td>
      <td className="px-2 py-2 text-right font-semibold text-slate-700 whitespace-nowrap">
        {totalCostHt > 0 ? formatCurrency(totalCostHt) : '—'}
      </td>
      <td className="px-2 py-2 text-right font-semibold text-slate-900 whitespace-nowrap">{formatCurrency(totalCa)}</td>
      <td className="px-2 py-2 text-right font-semibold whitespace-nowrap">
        {globalCostRecovery != null ? (
          <span className={globalCostRecovery >= 100 ? 'text-emerald-700' : 'text-slate-700'}>
            {globalCostRecovery} %
          </span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right pr-4 font-semibold text-emerald-700">{globalEcoulement} %</td>
    </>
  );
}
